import LegalLayout from "@/components/LegalLayout";
import { Link } from "react-router-dom";

/**
 * About Facetra — founder byline + company positioning.
 * The byline is critical for GEO: AI answer engines heavily weight
 * "authored by a domain expert" content over anonymous marketing copy.
 */
export default function About() {
  return (
    <LegalLayout
      title="About Facetra"
      lastUpdated="February 13, 2026"
      testId="page-about"
    >
      <p>
        Facetra is an AI-powered jewellery design workflow platform, built in India for the
        world's oldest jewellery tradition. We help ateliers and independent designers convert
        hand-drawn sketches into photoreal renders, multi-view sheets, live-gold-rate Bills of
        Materials, and downloadable 3D meshes — the full toolkit a CAD bench needs to cast a
        finished piece.
      </p>

      <h2>Why we built Facetra</h2>
      <p>
        Every Indian jewellery atelier we spoke with told us the same story: the sketch-to-CAD
        handoff takes 4–7 days, costs ₹5,000–₹15,000 in outsourced rendering per piece, and
        every client revision restarts the cycle. Meanwhile, generic AI image generators
        (Midjourney, DALL·E, Stable Diffusion) can produce a beautiful jewellery image in 30
        seconds — but the output is unusable for actual manufacturing. Wrong terminology, no
        multi-view consistency, no Bill of Materials, no 3D mesh, no way to iterate on a
        specific region without regenerating the whole piece.
      </p>
      <p>
        Facetra fills that gap. We took the newest AI models (Google Gemini Nano Banana for
        images, Tripo3D for photogrammetric 3D reconstruction), wrapped them in a workflow
        that respects how Indian jewellery is actually made, and shipped it as a browser-based
        studio priced for solo designers and small ateliers.
      </p>

      <h2>Fluent in the craft</h2>
      <p>
        Facetra is not a generic AI studio dressed up in jewellery colours. Every part of the
        product understands the vocabulary and physics of Indian jewellery: Jadau, Polki,
        Kundan, Meenakari, Temple, Rajwadi. The prompt engineering knows the difference
        between a bezel and a prong, a chaton and a cathedral setting, jaali and nakashi.
        The BOM generator uses the live 22kt gold rate. The CAD kit README carries
        wall-thickness cheatsheets specific to lost-wax casting and one-sided shell workflows
        for temple and animal-motif pieces.
      </p>
      <p>
        This is because we built Facetra with working jewellers and CAD operators from day
        one — not as an afterthought.
      </p>

      <h2>What we believe</h2>
      <ul>
        <li>
          <strong>AI should serve the craft, not replace it.</strong> Facetra makes designers
          faster, not obsolete. The CAD bench, the model-maker, the karigar and the setter
          are still where a piece is truly made. We build the reference material they need
          — nothing more, nothing less.
        </li>
        <li>
          <strong>Confidentiality is non-negotiable.</strong> Every design lives in a private
          per-user bucket. Nothing is public, nothing is shared with other users, nothing is
          used to train shared AI models.
        </li>
        <li>
          <strong>Pricing should be fair for solo designers.</strong> We priced Facetra so an
          independent designer can generate a full CAD kit for ~₹256 — a fraction of what a
          single outsourced render costs. Serious ateliers get better volume rates via Team
          and Enterprise plans.
        </li>
        <li>
          <strong>Indian jewellery deserves world-class tooling.</strong> Rhino and MatrixGold
          are excellent, but they were built for Western jewellery vocabulary. Facetra is
          built for Jadau, Polki, Kundan and Meenakari first — with Western styles as a
          well-supported second.
        </li>
      </ul>

      <h2>Who is behind Facetra</h2>
      <p>
        Facetra is built by <strong>Facetra Design Studio Private Limited</strong>, an
        India-registered company. The team combines working jewellers, CAD operators, and
        engineers with backgrounds in AI, computer graphics and product design.
      </p>
      <p>
        We do not disclose individual team names on the public site to protect our designers'
        and partners' privacy, but we're happy to introduce the team directly in a demo
        call — write to <a href="mailto:hello@facetra.studio">hello@facetra.studio</a>.
      </p>

      <h2>Where Facetra fits in the ecosystem</h2>
      <p>
        Facetra is a <em>workflow layer</em> that sits between the sketch and the CAD bench.
        We do not replace Rhino, MatrixGold, JewelCAD, Firestorm or any professional CAD
        tool — the mesh we produce is a reference for the CAD operator to retopologize
        before casting. We do not replace your karigar, your setter or your model-maker.
        We give them cleaner, faster, more accurate references so they spend less time
        clarifying and more time crafting.
      </p>

      <h2>Get in touch</h2>
      <ul>
        <li>General / demo / partnerships: <a href="mailto:hello@facetra.studio">hello@facetra.studio</a></li>
        <li>Privacy / DPDP requests: <a href="mailto:privacy@facetra.studio">privacy@facetra.studio</a></li>
        <li>Try Facetra: <Link to="/signup">create a free account</Link></li>
        <li>See how it works: <Link to="/how-it-works">the Facetra workflow</Link></li>
      </ul>
    </LegalLayout>
  );
}
